import { type CSSProperties } from 'react';
import Graph from './graph';
import { _js } from '@ifixit/localize';
import { SectionHeading } from './section_heading';
import { VoteHistory, type VoteHistoryDataType } from '../vote_history';
import { Table } from '@ifixit/ui/components/table';
import { Card } from '@ifixit/ui/components/deprecated-card';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';

// Type definitions
interface RepData {
   date: number;
   rep: number;
}

interface BreakdownItem {
   doctype: string;
   event: string;
   eventObject?: {
      title: string;
   };
   timesReceived: number;
   totalRep: number;
}

interface BadgeObject {
   name: string;
   classname: string;
}

interface BadgeData {
   badge: {
      badge: BadgeObject;
   };
   badgeTier: 'bronze' | 'silver' | 'gold';
   badgeTierTitle?: string;
   'translated-name': string;
   'translated-description': string;
   iconSvg: string;
   counter: number;
}

interface ReputationData {
   voteHistoryData?: VoteHistoryDataType;
   graphData: RepData[];
   reputationBreakdown: BreakdownItem[];
   badges: BadgeData[];
}

export function Reputation({ userid, data }: { userid: number; data: ReputationData }) {
   return (
      <div className="flex flex-col flex-1 w-full gap-6">
         <SectionHeading>{_js('Reputation')}</SectionHeading>
         {data.voteHistoryData && <VoteHistory userid={userid} {...data.voteHistoryData} />}
         <ReputationGraph graphData={data.graphData} />
         <ReputationBreakdown breakdown={data.reputationBreakdown} />
         <BadgesList badges={data.badges} />
      </div>
   );
}

const ReputationGraph = ({ graphData }: { graphData: RepData[] }) => (
   <div className="w-full h-[400px]">
      <Graph graphData={graphData} />
   </div>
);

const ReputationBreakdown = ({ breakdown }: { breakdown: BreakdownItem[] }) => (
   <>
      <h3 className="heading-24">{_js('Reputation Breakdown')}</h3>
      <BreakdownDisplay breakdown={breakdown} />
   </>
);

const BreakdownDisplay = ({ breakdown }: { breakdown: BreakdownItem[] }) =>
   breakdown.length > 0 ? (
      <BreakdownTable breakdown={breakdown} />
   ) : (
      <p id="reputationBreakdownHolder">{_js('No reputation gained yet.')}</p>
   );

const BreakdownTable = ({ breakdown }: { breakdown: BreakdownItem[] }) => {
   const cells = [
      { id: 'event', label: _js('Event') },
      { id: 'count', label: _js('Count') },
      { id: 'delta', label: _js('Delta') },
   ];

   const tableData = breakdown.map(line => ({
      id: `${line.doctype}-${line.event}`,
      event: line.eventObject?.title,
      count: line.timesReceived,
      delta: line.totalRep,
   }));

   return <Table data={tableData} cells={cells} />;
};

const BadgesList = ({ badges }: { badges: BadgeData[] }) => {
   const showBadges = badges.length > 0;

   if (!showBadges) {
      return null;
   }

   return (
      <>
         <SectionHeading>{_js('Badges')}</SectionHeading>
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {badges.map((userBadge, i) => (
               <BadgeItem key={i} badgeData={userBadge} />
            ))}
         </div>
      </>
   );
};

const BADGE_TIER_STYLES = {
   bronze: {
      backgroundColor: '#F5E8D0',
      color: '#974826',
      iconColor: '#CD7F32',
      iconLevel: '1',
   },
   silver: {
      backgroundColor: '#E3E3E3',
      color: '#585858',
      iconColor: '#969696',
      iconLevel: '2',
   },
   gold: {
      backgroundColor: '#FFE944',
      color: '#734310',
      iconColor: '#CE9300',
      iconLevel: '3',
   },
} as const;

const BadgeThumbnail = ({ badgeData }: { badgeData: BadgeData }) => (
   <div
      className="size-[56px] flex items-center justify-center"
      // iconSvg is server-generated by Badge::getBadgeIconSvg() from hardcoded values only
      dangerouslySetInnerHTML={{ __html: badgeData.iconSvg }}
   />
);

const BadgeTierBadge = ({ badgeData }: { badgeData: BadgeData }) => {
   const tierStyles = BADGE_TIER_STYLES[badgeData.badgeTier];
   return (
      <div className="flex items-center gap-1">
         <Badge>
            <FaIcon name="badge-check" style="solid" />
            {badgeData.counter}
         </Badge>
         <Badge
            style={
               {
                  backgroundColor: tierStyles.backgroundColor,
                  color: tierStyles.color,
                  '--icon-color': tierStyles.iconColor,
               } as CSSProperties
            }
         >
            <FaIcon
               name={`circle-${tierStyles.iconLevel}`}
               style="solid"
               className="inline-block [color:var(--icon-color)]"
            />
            {badgeData.badgeTierTitle?.split(':')[0]}
         </Badge>
      </div>
   );
};

const BadgeItem = ({ badgeData }: { badgeData: BadgeData }) => {
   const badgeObject = badgeData.badge.badge;

   return (
      <Card variant="product" url={`/Badge/${badgeObject.classname}`}>
         <BadgeThumbnail badgeData={badgeData} />
         <div className="flex flex-col flex-1 gap-1 line-height-normal">
            <div className="font-semibold">{badgeData['translated-name']}</div>
            <div>{badgeData['translated-description']}</div>
            <div className="mt-1.5">
               <BadgeTierBadge badgeData={badgeData} />
            </div>
         </div>
      </Card>
   );
};
